import React, { Component } from 'react';
import { loadScript } from '@ecs/tng-common/scripts/helpers';
import { QuidgetTypes, QuidgetContent } from '../../helpers/interfaces';
import {
  generateQuidgetContent,
  addDisclosureAnchorIcon
} from '../../helpers/single-post-replacements';
import parse from 'html-react-parser';
import BlogAccordion from './BlogAccordion';

interface QuidgetProps {
  quidget: QuidgetTypes;
  node: any;
  postData: {
    id: string;
    catSlug: string;
    catId: string;
    isSEMtemplate: boolean;
  };
}
interface QuidgetTypeState {
  scriptLoaded: boolean;
}

class Quidget extends Component<QuidgetProps, QuidgetTypeState> {
  constructor(props: QuidgetProps) {
    super(props);
    this.state = {
      scriptLoaded: false
    };
  }

  componentDidMount() {
    if (typeof window !== 'undefined' && this.props.quidget) {
      this.initializeWidget();
    }
  }

  componentWillUnmount() {
    const el = document.getElementById('quidget-async');
    if (el) {
      document?.head?.removeChild(el);
      this.setState({ scriptLoaded: false });
    }
  }

  getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return '';
  };

  getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      conf: params.get('conf') || '',
      mkid: params.get('mkid') || ''
    };
  };

  updateQuidgetTokens = (v2: string) => {
    const quidgetElement = document.getElementById('quidget');
    const src = '694332';
    const linkTextValue = 'View Details';

    if (quidgetElement) {
      const tokens = quidgetElement.querySelectorAll('.sh-bnk-quidget-markup');
      tokens.forEach((token) => {
        if (token instanceof HTMLElement) {
          // Get data-content attribute
          const content = token.getAttribute('data-content');
          if (content) {
            try {
              let dataContentUpdated = content;

              // Always add *src* after the opening brace '{'
              dataContentUpdated = dataContentUpdated.replace(/{/, `{*src*:*${src}*,`);

              // Check if *field*:*ProductUrlWithButton* exists
              if (dataContentUpdated.includes('*field*:*ProductUrlWithButton*')) {
                // Replace displayname in *linkText*:*displayname* with the custom value
                dataContentUpdated = dataContentUpdated.replace(
                  /[*]linkText[*]:[*]displayname[*]/g,
                  `*linkText*:*${linkTextValue}*`
                );
              }

              // Check if *var2* exists in content string; if not, add it
              if (dataContentUpdated.includes('var2')) {
                // Replace existing *var2* value
                dataContentUpdated = dataContentUpdated.replace(
                  /[*]var2[*]:[*][^,]*[*]/g,
                  `*var2*:*${v2}*`
                );
              } else {
                // Add *var2* if not present, inserting it before the closing brace
                dataContentUpdated = dataContentUpdated.replace(/\}$/, `, *var2*:*${v2}*}`);
              }
              token.setAttribute('data-content', dataContentUpdated);
            } catch (error) {
              console.error('Failed to parse token content:', error);
            }
          } else {
            console.warn('Token does not have data-content attribute:', token);
          }
        } else {
          console.warn('Token is not an HTMLElement:', token);
        }
      });
    }
  };

  initializeWidget() {
    const queryParams = this.getQueryParams();
    const mkid =
      queryParams.mkid || this.getCookie('marketingid') || '00000000-0000-0000-0000-000000000000';
    let counter = 0;
    const int = setInterval(() => {
      if (window.ECS?.tms?.data) {
        clearInterval(int);
        const v2 = {
          id: this.props.postData.id ? `id:${this.props.postData.id}` : '',
          cat: this.props.postData.catSlug ? `|cat:${this.props.postData.catSlug}` : '',
          pc: window.ECS?.tms?.data.pc ? `|pc:${window.ECS?.tms?.data.pc}` : '',
          cc: window.ECS?.tms?.data.cc ? `|cc:${window.ECS?.tms?.data.cc}` : '',
          mkid: `|mkid:${mkid}`,
          conf: queryParams.conf ? `|conf:${queryParams.conf}` : ''
        };
        // Add the v2 to each token
        this.updateQuidgetTokens(`${v2.id}${v2.cat}${v2.pc}${v2.cc}${v2.mkid}${v2.conf}`);
        this.load();
      } else if (counter > 15) {
        clearInterval(int);
      }
      counter++;
    }, 100);
  }

  load() {
    const scriptSrc = `${process.env.QUIDGET_HOST}`;
    const scriptId = 'quidget-async';
    loadScript(scriptSrc, scriptId)
      .then(() => {
        if (window.shBankingQuidget && typeof window.shBankingQuidget.initialize === 'function') {
          console.log('Quidget script loaded successfully');
          window.shBankingQuidget.initialize();
          this.setState({ scriptLoaded: true });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  render() {
    if (!this.props.quidget) return null;
    const content = generateQuidgetContent(this.props.node);
    return (
      <div
        id="quidget"
        className="my-4">
        {content &&
          content.map((item: QuidgetContent, index: number) => (
            <div
              className={`border rounded ${content.length - 1 !== index ? 'mb-3' : ''}`}
              key={item.id}>
              <div className="px-3 px-lg-4 pt-3 pt-lg-4">
                <div className="d-flex flex-column align-items-start gap-3">
                  {item.isPick && (
                    <p className="pick-label body-text-sm fw-semibold bg-alert rounded-pill mb-0 py-1 px-3">
                      Overall pick<span className="ms-2 mb-0">{addDisclosureAnchorIcon()}</span>
                    </p>
                  )}
                  {item.tag && (
                    <h3 className="h7 text-wrap px-3 py-2 mb-0 label label-blue text-uppercase">
                      {item.tag}
                    </h3>
                  )}
                </div>
                <div className="logo py-4 d-md-flex text-center align-items-center justify-content-between">
                  {item.logo &&
                    parse(item.logo, {
                      replace: (domNode) => {
                        if (domNode.type === 'tag' && domNode.name === 'img' && domNode.attribs) {
                          const newAttribs = {
                            ...domNode.attribs,
                            class:
                              (domNode.attribs.class || '') +
                              ' img-fluid mx-auto mx-md-0 d-block mb-3 mb-md-0'
                          };
                          domNode.attribs = newAttribs;
                          return domNode;
                        }
                        return domNode;
                      }
                    })}
                  <div className="d-flex flex-column align-items-center">
                    {item.action &&
                      item.action.link &&
                      parse(item.action.link, {
                        replace: (domNode) => {
                          if (domNode.type === 'tag' && domNode.name === 'a' && domNode.attribs) {
                            const newAttribs = {
                              ...domNode.attribs,
                              class:
                                (domNode.attribs.class || '') +
                                ' btn btn-secondary px-md-5 btn-min-width w-100 w-md-auto'
                            };
                            domNode.attribs = newAttribs;
                            return domNode;
                          }
                          return domNode;
                        }
                      })}
                    {item.action && item.action.text && (
                      <span className="mt-2 text-gray body-text-sm">{parse(item.action.text)}</span>
                    )}
                  </div>
                </div>
                <div className="d-lg-flex justify-content-between border-top border-bottom">
                  <div className="py-3 py-lg-4 pe-lg-4 col-12 col-lg-6">
                    {item.overview && item.overview.title && (
                      <p className="h6 mb-2 text-midnight-blue">{item.overview.title}</p>
                    )}
                    {item.overview && item.overview.content}
                  </div>
                  <div className="details pt-3 pb-2 pt-lg-4 pb-lg-3 ps-lg-4 col-12 col-lg-6">
                    <table>
                      <tbody>
                        {item.details &&
                          item.details.map((detail, index) => (
                            <tr key={index}>
                              <th className="align-top">
                                <p className="mb-2 text-gray">
                                  {Array.isArray(detail.category)
                                    ? detail.category?.map((item, i) => (
                                        <React.Fragment key={i}>
                                          {React.isValidElement(item)
                                            ? React.cloneElement(
                                                item as React.ReactElement,
                                                (() => {
                                                  const props = (item as React.ReactElement).props;
                                                  const hasInfoIcon =
                                                    typeof props?.className === 'string' &&
                                                    props.className.includes(
                                                      'ecs-font-icon-act-info'
                                                    );

                                                  return hasInfoIcon
                                                    ? { className: `${props.className} ms-2` }
                                                    : {};
                                                })()
                                              )
                                            : typeof item === 'string'
                                              ? item
                                              : null}
                                        </React.Fragment>
                                      ))
                                    : detail.category}
                                </p>
                              </th>
                              <td className="ps-3">
                                <p className="h6 mb-2">{parse(detail.value || '')}</p>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="pt-3 pt-lg-4 pe-lg-4 border-bottom">
                  {item.pick && item.pick.title && (
                    <p className="h6 mb-2 text-midnight-blue">{item.pick.title}</p>
                  )}
                  {item.pick && item.pick.content}
                </div>
                {item.accordion && item.accordion.length > 0 && (
                  <BlogAccordion
                    data={item.accordion}
                    id={`${this.props.quidget}-${item.id}-acc`}
                  />
                )}
              </div>
            </div>
          ))}
      </div>
    );
  }
}

export default Quidget;
